import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { CommandConversationService } from '../../../services/command-conversation.service';
import { EditSessionRegistry } from '../../../shared/edit-session/edit-session-registry.service';
import { LookupApiService, ReportingIntervalLookup } from '../../../services/lookup-api.service';
import { defaultInterval, Interval, reportPeriodLabel } from '../overview-model';
import { OverviewApiService, OverviewPeriod, OverviewReport, OverviewTemplateDetail } from '../overview-api.service';

/**
 * One job per route. /generate runs the template on the chosen period (latest by default) of the chosen interval;
 * /edit and /new change a definition. Company templates edit in place; editing a system template saves a
 * company copy and leaves the system one unchanged.
 */
@Component({
  selector: 'app-report-template-detail',
  templateUrl: './report-template-detail.component.html',
  styleUrls: ['./report-template-detail.component.css'],
  host: { class: 'workspace-page--full' },
})
export class ReportTemplateDetailComponent implements OnInit, OnDestroy {
  @Output() readonly saved = new EventEmitter<void>();
  template?: OverviewTemplateDetail;
  form = { name: '', description: '', instructions: '', isActive: true };
  editing = false;
  loading = false;
  saving = false;
  error = '';
  /** Unsaved new template or company copy: its id is chosen once so retries never create duplicates. */
  private draftId?: string;
  intervals: ReportingIntervalLookup[] = [];
  interval: Interval = defaultInterval;
  /** Empty means the latest period; the choices are the periods with financial data for the interval. */
  period = '';
  periods: OverviewPeriod[] = [];
  readonly periodLabel = reportPeriodLabel;
  focus = '';
  target?: OverviewReport;
  targetLoading = false;
  targetError = '';
  private targetRequest?: Subscription;
  /** The template read in flight; a newer route cancels it so a late answer never fills another template's page. */
  private loadRequest?: Subscription;
  /** The form as last loaded or saved; editing that differs from it is unsaved. */
  private baseline = '';
  private readonly destroyed$ = new Subject<void>();
  /** Cancels this page's in-flight account-owned requests at a session change, like CampaignDetail. */
  private readonly cancelRequests$ = new Subject<void>();
  /** Releases the shared edit-session entry that also drives the browser unload prompt. */
  private readonly unregister: () => void;
  /** Read by UnsavedChangesGuard and EditSessionRegistry (in-app guard and the browser's own unload prompt). */
  get dirty(): boolean { return this.editing && !this.loading && (!!this.template || this.isNew) && JSON.stringify(this.form) !== this.baseline; }
  get isNew(): boolean { return !!this.draftId && this.template?.id !== this.draftId; }
  get canManage(): boolean { return this.auth.hasPermission('Company.Manage'); }
  get title(): string { return this.isNew ? (this.form.name || 'New template') : (this.template?.name ?? 'Template'); }

  get canGenerate(): boolean { return !!this.template?.isActive && !this.editing && !!this.target && !this.targetLoading; }
  /** Why Generate is disabled, shown on hover; empty while it can run. */
  get generateBlockedReason(): string {
    if (this.template && !this.template.isActive) return 'Archived templates cannot generate reports. Restore it first.';
    if (this.targetLoading) return 'Finding the period to analyze.';
    if (!this.target) return this.targetError || 'Choose a period with financial data.';
    return '';
  }
  get targetLabel(): string {
    return this.target ? this.target.data.snapshot.period.label + ' vs ' + this.target.data.snapshot.comparePeriod.label : '';
  }

  constructor(private readonly route: ActivatedRoute, private readonly router: Router,
    private readonly api: OverviewApiService, public readonly auth: AuthService,
    private readonly lookups: LookupApiService, private readonly conversation: CommandConversationService,
    registry: EditSessionRegistry) {
    this.unregister = registry.register(this);
  }

  ngOnInit(): void {
    this.interval = (this.route.snapshot.queryParamMap.get('interval') || defaultInterval) as Interval;
    if (this.route.snapshot.data['templateMode'] === 'generate') {
      this.lookups.reportingIntervals().pipe(takeUntil(this.destroyed$)).subscribe(items => this.intervals = items);
      this.loadTarget();
    }
    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      const id = params.get('id');
      this.loadRequest?.unsubscribe(); this.loading = false;
      this.error = ''; this.template = undefined; this.draftId = undefined;
      this.editing = this.route.snapshot.data['templateMode'] === 'edit';
      if (id) { this.load(id); return; }
      this.draftId = crypto.randomUUID();
      this.form = { name: '', description: '', instructions: '', isActive: true };
      this.baseline = JSON.stringify(this.form);
    });
    // Invalidate the prior account's editor state in place rather than navigating: a dirty guard could cancel a
    // navigation and leave another tenant's template/target on screen. Mirrors CampaignDetail's session-change reset.
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.cancelRequests$.next();
      this.loadRequest?.unsubscribe(); this.targetRequest?.unsubscribe();
      this.template = undefined; this.target = undefined; this.draftId = undefined;
      this.form = { name: '', description: '', instructions: '', isActive: true };
      this.baseline = JSON.stringify(this.form);
      this.editing = false; this.loading = false; this.saving = false;
      this.targetLoading = false; this.targetError = ''; this.periods = []; this.focus = '';
      this.error = 'Account changed. Reopen the template to continue.';
    });
  }
  load(id: string): void {
    this.loadRequest?.unsubscribe();
    this.loading = true;
    this.loadRequest = this.api.templateDetail(id).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: template => {
        this.loading = false; this.template = template; this.reset();
        // A system template is never edited; editing it saves a company copy.
        if (this.editing && template.isSystem) {
          this.draftId = crypto.randomUUID();
          this.form = { ...this.form, name: template.name + ' (company)' };
        }
        this.baseline = JSON.stringify(this.form);
      },
      error: () => { this.loading = false; this.error = 'This template could not be loaded.'; },
    });
  }
  /** Returns to the template list, like Back to Customers on a customer page. Save and Archive return there too. */
  back(): void { void this.router.navigate(['/overview/templates']); }
  save(): void {
    if (this.saving || !this.form.name.trim() || !this.form.instructions.trim()) return;
    const creating = this.isNew;
    const id = creating ? this.draftId! : this.template!.id;
    this.saving = true; this.error = '';
    this.api.saveTemplate(id, { revision: creating ? 0 : this.template!.revision, name: this.form.name.trim(),
      description: this.form.description.trim(), instructions: this.form.instructions.trim(), isActive: this.form.isActive })
      .pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
        next: () => {
          // Inputs are read-only while saving, so the form is exactly what was saved.
          this.saving = false; this.baseline = JSON.stringify(this.form); this.saved.emit(); this.back();
        },
        error: (error: unknown) => {
          this.saving = false;
          this.error = error instanceof HttpErrorResponse && typeof error.error?.message === 'string' ? error.error.message : 'The template could not be saved.';
        },
      });
  }
  /** Archiving keeps the template for the reports made from it and stops new ones; restoring reverses it. */
  setArchived(archived: boolean): void {
    const template = this.template;
    if (!template || template.isSystem || !this.canManage || this.saving) return;
    this.saving = true; this.error = '';
    this.api.saveTemplate(template.id, { revision: template.revision, name: template.name, description: template.description,
      instructions: template.instructions, isActive: !archived }).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: () => { this.saving = false; this.saved.emit(); this.back(); },
      error: (error: unknown) => {
        this.saving = false;
        this.error = error instanceof HttpErrorResponse && typeof error.error?.message === 'string' ? error.error.message
          : archived ? 'The template could not be archived.' : 'The template could not be restored.';
      },
    });
  }
  changeInterval(value: Interval): void { this.interval = value; this.period = ''; this.periods = []; this.loadTarget(); }
  changePeriod(value: string): void { this.period = value; this.loadTarget(); }
  loadTarget(): void {
    this.targetRequest?.unsubscribe(); this.targetLoading = true; this.targetError = ''; this.target = undefined;
    this.targetRequest = this.api.report({ interval: this.interval, period: this.period || undefined, comparison: 'year' }).pipe(takeUntil(this.cancelRequests$), takeUntil(this.destroyed$)).subscribe({
      next: report => { this.targetLoading = false; this.target = report; this.periods = report.periods; },
      error: (error: unknown) => {
        this.targetLoading = false;
        this.targetError = error instanceof HttpErrorResponse && error.status === 404
          ? 'No financial data is available for this interval.' : 'This period could not be loaded.';
      },
    });
  }
  /** Generate is the explicit request: it starts a Bree turn through the existing conversation send path. */
  generate(): void {
    const template = this.template, target = this.target;
    if (!template || !target || !this.canGenerate) return;
    const label = target.data.snapshot.period.label;
    const prompt = [
      `Generate a "${template.name}" report for ${label} and publish it to my Analysis & Reports.`,
      `Template: ${template.id}, revision ${template.revision}. Read it with get_overview_template and publish with this templateId and the revision you read.`,
      `Financial report: ${target.id} (${target.interval} starting ${target.periodStart}, comparison ${target.comparison}).`,
      ...(this.focus.trim() ? [`Focus for this report: ${this.focus.trim()}`] : []),
    ].join('\n');
    this.conversation.newConversation();
    this.conversation.renameConversation(this.conversation.activeConversation.id, `${template.name} · ${label}`);
    this.conversation.open();
    void this.conversation.send(prompt);
    void this.router.navigate(['/overview']);
  }
  private reset(): void {
    if (this.template) this.form = { name: this.template.name, description: this.template.description,
      instructions: this.template.instructions, isActive: this.template.isActive };
  }
  ngOnDestroy(): void {
    this.unregister();
    this.cancelRequests$.next(); this.cancelRequests$.complete();
    this.destroyed$.next(); this.destroyed$.complete();
  }
}
